import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { API_HEADERS, ENDPOINTS } from "../../constants/api";
import { baseQueryWithAuthCheck } from "./helper";

import { callFromMiddleware, extractDomain } from "../../utils/index";
import { getUserDetails } from "../../utils/getUserDetails";
import {
  CandidatePipeline,
  GetPollingPayload,
  GetPollingResponse,
} from "../../types/custom-types";

const baseQuery = fetchBaseQuery({
  baseUrl: window.location.origin,
  headers: API_HEADERS,
  credentials: "include",
});

const userDetails = getUserDetails();
const extraHeaders = {
  "X-CHANNEL-ID": userDetails?.channel_id,
  "X-Language-Code": "EN",
  "X-SITECONTEXT": userDetails?.site_context,
  "X-SUBCHANNEL-ID": userDetails?.subhchannel_id,
  "X-Source-Country": "IN",
  "X-Source-Site-Context": "rexmonster",
};

export const mjpApi = createApi({
  reducerPath: "mjp",
  baseQuery: baseQueryWithAuthCheck(baseQuery),
  tagTypes: ["jobs"],
  endpoints: (builder) => ({
    getJobs: builder.query<any, object>({
      query: (args) => ({
        url: callFromMiddleware(ENDPOINTS.jobs),
        method: "GET",
        headers: API_HEADERS,
        params: {
          ...args,
        },
      }),
      providesTags: ["jobs"],
    }),
    deleteJob: builder.mutation<any, number[]>({
      query: (jobId) => ({
        url: callFromMiddleware(`${ENDPOINTS.deleteJob}?folderId=${jobId}`),
        method: "DELETE",
        headers: API_HEADERS,
        // body: {
        //   jobId,
        // },
      }),
      invalidatesTags: (_, error) => (error ? [] : ["jobs"]),
    }),
    expireJob: builder.mutation<any, number[]>({
      query: (jobId) => ({
        url: callFromMiddleware(ENDPOINTS.expireJob),
        method: "POST",
        headers: API_HEADERS,
        body: {
          jobId,
        },
      }),
      invalidatesTags: (_, error) => (error ? [] : ["jobs"]),
    }),
    getRenewJobDetails: builder.mutation<any, number[]>({
      query: (jobId) => ({
        url: callFromMiddleware(ENDPOINTS.renewJobDetails),
        method: "POST",
        headers: API_HEADERS,
        body: {
          jobId,
        },
      }),
      invalidatesTags: (_, error) => (error ? [] : ["jobs"]),
    }),
    renewJob: builder.mutation<
      any,
      { jobId: number; autoRenew: boolean; expiryEnabled: boolean }[]
    >({
      query: (payload) => ({
        url: callFromMiddleware(ENDPOINTS.renewJob),
        method: "PUT",
        headers: API_HEADERS,
        body: [...payload],
      }),
      invalidatesTags: (_, error) => (error ? [] : ["jobs"]),
    }),
    downloadExcel: builder.query({
      query: (args) => ({
        url: callFromMiddleware(ENDPOINTS.downloadExcel),
        method: "GET",
        headers: API_HEADERS,
        params: args,
      }),
    }),
    discardDraft: builder.mutation<any, number>({
      query: (jobId) => ({
        url: callFromMiddleware(ENDPOINTS.discardDraft),
        method: "POST",
        headers: API_HEADERS,
        body: {
          jobId,
        },
      }),
      invalidatesTags: (_, error) => (error ? [] : ["jobs"]),
    }),
    getCandidatePipelineCount: builder.query<
      Partial<CandidatePipeline>,
      number
    >({
      query: (args) => ({
        url: callFromMiddleware(`${ENDPOINTS.getCandidatePipeline}/${args}`),
        method: "POST",
        headers: API_HEADERS,
        params: {},
        body: {},
      }),
    }),
    checkInventory: builder.query({
      query: (args) => ({
        url: callFromMiddleware(ENDPOINTS.checkInventory),
        method: "POST",
        body: args,
        headers: API_HEADERS,
        refetchOnMountOrArgChange: true,
      }),
    }),
    checkEmail: builder.query<any, void>({
      query: (args) => ({
        url: callFromMiddleware(ENDPOINTS.checkEmail),
        method: "GET",
        headers: API_HEADERS,
      }),
    }),
    downloadExcelNew: builder.query<any, any>({
      query: (args) => ({
          url: callFromMiddleware(ENDPOINTS.downloadExcelNew),
          method: "POST",
          headers: API_HEADERS,
          body:args
        }),
    }),
    getDownloadProfilesPolling: builder.query<
      any,
      any
    >({
      query: (args) => ({
        url: callFromMiddleware(ENDPOINTS.getDownloadProfilesPolling),
        method: "POST",
        headers: API_HEADERS,
        params: {},
        body: args,
      }),
    }),
    trackHealth : builder.query<any, any>({
      query: (body) => ({
        url: callFromMiddleware(ENDPOINTS.healthTracker),
        method: "POST",
        headers: API_HEADERS,
        body: body,
      }),
    }),
  }),
});

export const {
  useLazyGetJobsQuery,
  useDeleteJobMutation,
  useExpireJobMutation,
  useGetRenewJobDetailsMutation,
  useRenewJobMutation,
  useLazyDownloadExcelQuery,
  useDiscardDraftMutation,
  useLazyGetCandidatePipelineCountQuery,
  useLazyCheckInventoryQuery,
  useGetDownloadProfilesPollingQuery,
  useLazyDownloadExcelNewQuery,
  useLazyCheckEmailQuery,
  useLazyTrackHealthQuery,
} = mjpApi;

export default mjpApi;
